import React, { useCallback, useContext, useMemo, useState } from 'react';
import Layout from '../../components/layout/main';
import { Fade, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getDataFull, deleteData } from '../../services/ajax/';
import { AuthContext } from '../../context/auth';
import { GlobalsContext } from '../../context/globals';
import MaterialTable, { DataType } from '../../components/material-table';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { blue, green, red, teal } from '@mui/material/colors';
import { useSnackbar } from 'notistack';
import { AlertModalContext } from '../../components/modal/alert';
import { LoadingModalContext } from '../../components/modal/loading';

function Page() {
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const globalsContext = useContext(GlobalsContext);
    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);
    const { enqueueSnackbar } = useSnackbar();

    const [rowsTableMain, setRowsTableMain] = useState(null);
    let [tableMainNeedUpdate, setTableMainNeedUpdate] = useState(Date.now());

    const onNuevo = useCallback(() => {
        globalsContext.setIdEdit(false);
        navigate('/personal/nuevo');
    }, [globalsContext, navigate]);

    const onEditar = useCallback((row) => {
        globalsContext.setIdEdit(row.id_personal);
        navigate('/personal/editar');
    }, [globalsContext, navigate]);

    const onDetalle = useCallback((row) => {
        globalsContext.setIdEdit(row.id_personal);
        navigate('/personal/detalle');
    }, [globalsContext, navigate]);

    const onEliminar = useCallback((row) => {
        alertModal.showModal(
            'Eliminar personal',
            'El empleado ' + (row.nombre_apellido || '') + ' será eliminado. ¿Deseas continuar?',
            async function () {
                loadingModal.setOpen(true);
                const response = await deleteData(`personal/${row.id_personal}`, usuario.token);
                if (response.error === '') {
                    enqueueSnackbar('Personal eliminado', { variant: 'success' });
                    setTableMainNeedUpdate(Date.now());
                } else {
                    enqueueSnackbar(response.error, { variant: 'error' });
                }
                loadingModal.setOpen(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usuario.token]);

    const tableMain = useMemo(() => ({
        title: 'Personal',
        idName: 'id_personal',
        defaultOrder: 'asc',
        headers: [
            { id: 'legajo', label: 'Legajo', dataType: DataType.TEXT, sortable: true },
            { id: 'nombre_apellido', label: 'Apellido y nombre', dataType: DataType.TEXT, sortable: false },
            { id: 'dni', label: 'DNI', dataType: DataType.TEXT, sortable: false },
            { id: 'estado', label: 'Estado', dataType: DataType.TEXT, sortable: false },
        ],
        loadData: async (orderBy, order, page, rowsPerPage, searchText, selectedFilter, pageUpdate, totalUpdate, filterListUpdate) => {
            setRowsTableMain(null);
            const params = new URLSearchParams();
            params.append('per_page', rowsPerPage.toString());
            params.append('page', (page + 1).toString());
            if (orderBy) params.append('order_by', orderBy);
            if (order) params.append('order', order);
            if (searchText) params.append('search_text', searchText);

            const response = await getDataFull(`personal?${params.toString()}`, usuario.token);
            if (response.error === '') {
                const tablePage = response.meta?.page ? response.meta.page - 1 : page;
                pageUpdate(tablePage);
                totalUpdate(response.meta?.total || 0);
                filterListUpdate([]);
                setRowsTableMain(response.data || []);
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        },
        moreMenuItems: [
            {
                label: 'Cargar nuevo empleado',
                secondary: 'Alta de personal del Ministerio',
                icon: <AddCircleOutlineRoundedIcon sx={{ color: green[600] }} />,
                itemColor: green[600],
                action: () => onNuevo(),
            },
        ],
        rowActions: (row) => ([
            {
                label: 'Ver detalle y credenciales',
                secondary: 'Vinculaciones, función y credenciales QR',
                icon: <VisibilityRoundedIcon sx={{ color: teal[600] }} />,
                itemColor: teal[600],
                action: () => onDetalle(row),
            },
            {
                label: 'Modificar datos',
                secondary: 'Editar datos personales y estado',
                icon: <EditRoundedIcon sx={{ color: blue[600] }} />,
                itemColor: blue[600],
                action: () => onEditar(row),
            },
            {
                label: 'Eliminar personal',
                secondary: 'Se marca como eliminado.',
                icon: <DeleteRoundedIcon sx={{ color: red[600] }} />,
                itemColor: red[600],
                dividerBefore: true,
                action: () => onEliminar(row),
            },
        ]),
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [usuario.token]);

    return (
        <Layout nombrePagina="Personal">
            <Fade in={true} timeout={900}>
                <Grid size={12}>
                    <MaterialTable
                        title={tableMain.title}
                        loadData={tableMain.loadData}
                        headers={tableMain.headers}
                        data={rowsTableMain}
                        moreMenuItems={tableMain.moreMenuItems}
                        selectable={false}
                        rowActions={tableMain.rowActions}
                        idName={tableMain.idName}
                        defaultOrder={tableMain.defaultOrder}
                        defaultRowsPerPage={5}
                        needUpdate={tableMainNeedUpdate}
                    />
                </Grid>
            </Fade>
        </Layout>
    );
}

export default Page;
